// Central source of truth for brand & site data (VRP Flooring LLC).
// Company facts sourced from vrpflooringllc.com.

export const BRAND = {
  name: "VRP Flooring",
  legalName: "VRP Flooring LLC",
  tagline: "Quality flooring, installed right the first time.",
  founded: 2001,
  phone: "+1 203-617-7324",
  phoneDisplay: "(203) 617-7324",
  email: "vrpservicesllc@outlook.com",
  website: "https://vrpflooringllc.com",
  address: {
    street: "6 Whitney Ave",
    city: "Danbury",
    region: "CT",
    regionName: "Connecticut",
    postalCode: "06810",
    country: "US",
  },
  hours: [
    { day: "Mon – Sun", time: "Open 24 Hours" },
  ],
  hoursSchema: [
    {
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
  ],
  social: {
    instagram: "https://www.instagram.com/vrpflooring/?hl=en",
    facebook: "https://www.facebook.com/vrpflooring",
    google: "https://www.google.com/search?q=vrp+flooring+connecticut&rlz=1C5CHFA_enIE912IE912&oq=vrp&gs_lcrp=EgZjaHJvbWUqBggCECMYJzIJCAAQRRg5GIAEMgYIARAjGCcyBggCECMYJzIJCAMQABgKGIAEMgcIBBAAGIAEMgcIBRAAGIAEMgkIBhAAGAoYgAQyBggHEEUYPNIBCDIxMzBqMGo3qAIAsAIA&sourceid=chrome&ie=UTF-8",
  },
  credentials: ["Licensed & Insured", "Locally Owned & Operated", "Serving Connecticut Since 2001"],
  geo: { lat: 41.3948, lng: -73.454 }, // Danbury, CT
  primaryAreas: ["Danbury", "Fairfield County", "New Haven County", "Hartford County", "Connecticut"],
} as const;

export const VALUES = [
  { title: "Licensed Professionals", desc: "Licensed, insured, locally owned flooring contractors you can trust in your home." },
  { title: "Premium Materials", desc: "Hardwood, luxury vinyl, laminate and tile from every major brand and price point." },
  { title: "Attention to Detail", desc: "Straight seams, tight transitions, clean baseboards — the details that make a floor look custom." },
  { title: "Competitive Pricing", desc: "Honest, itemized pricing with financing available and no hidden line items." },
  { title: "Customer Satisfaction", desc: "A 100% satisfaction guarantee backed by hundreds of five-star Google reviews." },
];

export const DIFFERENTIATORS = [
  "Licensed Professionals — insured, locally owned and operated flooring contractors",
  "Premium Materials — all major brands, colors and textures in stock",
  "Attention to Detail — clean seams, precise cuts, spotless job sites",
  "Competitive Pricing — transparent estimates and financing available",
  "Customer Satisfaction — 100% satisfaction guarantee on every installation",
  "Free Estimates — shop-at-home samples with same-day appointments",
];

export const BENEFITS = [
  { title: "Licensed Professionals", desc: "A licensed, insured and fully vetted install crew in your home — never day labor." },
  { title: "Premium Materials", desc: "Hundreds of samples from every major flooring brand, in every color and texture." },
  { title: "Attention to Detail", desc: "Furniture moved with care, subfloors prepped properly, transitions finished clean." },
  { title: "Competitive Pricing", desc: "Clear written pricing at multiple quality levels, plus financing options." },
  { title: "Customer Satisfaction", desc: "4.6 stars across 267+ Google reviews and a 100% satisfaction guarantee." },
  { title: "Free Estimates", desc: "No-obligation in-home estimates with same-day appointments and next-day installation." },
];

export type Service = {
  slug: string;
  name: string;
  short: string;
  hero: string;
  priority: number;
  priceRange: string;
  includes: string[];
  problems: string[];
  projects: string[];
  materials: string[];
  process: { title: string; desc: string }[];
  priceFactors: string[];
  faqs: { q: string; a: string }[];
};

export const SERVICES: Service[] = [
  {
    slug: "luxury-vinyl-tile",
    name: "Luxury Vinyl Tile",
    short: "Waterproof luxury vinyl tile (LVT) installation with realistic stone and ceramic looks — built for kitchens, baths and entries.",
    hero: "The look of stone and tile, without the cold, cracks or grout headaches.",
    priority: 1,
    priceRange: "$4 – $9 per sq. ft. installed",
    includes: [
      "Luxury vinyl tile (LVT) installation, floating or glue-down",
      "Stone-look, slate-look, travertine and ceramic-look formats",
      "Subfloor leveling, patching and moisture barrier",
      "Removal and haul-away of old tile, vinyl, carpet or laminate",
      "Groutable LVT layouts with matching grout lines",
      "Diagonal, herringbone and pattern layouts",
      "Transitions, thresholds, base shoe and quarter-round",
      "Furniture moving, floor protection and full cleanup",
    ],
    problems: [
      "Cracked, loose or hollow ceramic tile with failing grout",
      "Cold, hard floors that are uncomfortable in kitchens and baths",
      "Water damage and staining from spills, pets, kids and leaks",
      "Uneven or hollow-sounding subfloors under existing tile",
      "Grout lines that trap dirt and never look clean",
      "Slippery, unsafe floors in entryways and mudrooms",
    ],
    projects: [
      "Kitchen and bathroom LVT",
      "Entryways, foyers and mudrooms",
      "Laundry rooms and powder rooms",
      "Basement waterproof flooring over concrete",
      "Rental and condo turnovers",
      "Whole-home LVT on one level",
      "Restaurant and light commercial entries",
    ],
    materials: [
      "Rigid-core SPC and WPC tile formats",
      "Stone-look, slate-look and travertine-look LVT",
      "Groutable tile formats with color-matched grout",
      "Attached acoustic underlayment for sound control",
      "20-mil and 30-mil commercial wear layers",
      "Glue-down LVT for permanent installations",
    ],
    process: [
      { title: "In-Home Consultation", desc: "We bring stone-look and ceramic-look LVT samples to your home, measure every room and assess the subfloor for moisture and flatness — no obligation." },
      { title: "Material & Layout Selection", desc: "Compare groutable vs. floating formats, pick the tile size and pattern (straight, diagonal or herringbone), and confirm the wear layer for your traffic level." },
      { title: "Subfloor Prep", desc: "We level high spots, patch low spots, apply a moisture barrier over concrete and address any hollow areas so the tile lays flat and quiet." },
      { title: "Removal & Demo", desc: "Old tile, vinyl, carpet or laminate comes up cleanly; we haul it away and dispose of it so your home stays clear." },
      { title: "Installation", desc: "Tiles are laid to the agreed pattern with tight, consistent grout lines (if groutable), proper expansion gaps and matched transitions at every doorway." },
      { title: "Final Walkthrough", desc: "We clean the floor, inspect every seam and transition with you, and walk through care instructions before we call it done." },
    ],
    priceFactors: [
      "Square footage and number of rooms",
      "Tile format and wear-layer thickness",
      "Subfloor leveling and moisture prep needed",
      "Removal and disposal of existing flooring",
      "Groutable vs. floating installation",
      "Pattern complexity (straight vs. diagonal/herringbone)",
      "Stairs, transitions and custom thresholds",
    ],
    faqs: [
      { q: "What is luxury vinyl tile?", a: "LVT is a rigid, 100% waterproof tile-format vinyl that replicates stone and ceramic with a warmer, softer feel underfoot." },
      { q: "Is LVT really waterproof?", a: "Yes. Rigid-core LVT is fully waterproof, which makes it ideal for kitchens, baths, laundry rooms and basements." },
      { q: "How much does LVT cost installed?", a: "Most LVT projects run $4–$9 per square foot installed depending on wear layer, format and subfloor prep." },
      { q: "Can LVT be grouted?", a: "Yes — groutable LVT gives you an authentic tile look with far more durability and comfort than ceramic, and we match the grout color to the tile." },
      { q: "How long does installation take?", a: "Most rooms are finished in 1–3 days, with next-day installation available on in-stock materials." },
      { q: "Can LVT go over my existing floor?", a: "Often yes, if the existing floor is flat and sound. Floating LVT can install over intact vinyl or laminate; we assess the subfloor during your free estimate." },
      { q: "Is LVT good for basements?", a: "It's one of the best choices for below-grade concrete slabs thanks to its moisture resistance and dimensional stability." },
      { q: "Do you remove the old flooring?", a: "Yes — removal and disposal of your existing floor is included in the estimate." },
      { q: "What's the difference between LVT and LVP?", a: "Same waterproof rigid-core technology. LVT comes in stone- and tile-look squares (often groutable); LVP comes in wood-look planks." },
      { q: "Does LVT need underlayment?", a: "Most rigid-core LVT has an attached acoustic underlayment. Over concrete we may add a moisture barrier depending on the slab." },
      { q: "Is LVT safe for pets and kids?", a: "Yes — it's waterproof, scratch resistant and free of the cold, hard surface that makes tile uncomfortable and slippery." },
      { q: "How do I clean and maintain LVT?", a: "Sweep or vacuum regularly, damp-mop with a neutral cleaner, and use felt pads under furniture. Avoid wax and steam mops." },
    ],
  },
  {
    slug: "hardwood-flooring",
    name: "Hardwood",
    short: "Solid and engineered hardwood flooring installation for lasting warmth, character and resale value.",
    hero: "Real wood floors, installed and finished to last decades.",
    priority: 2,
    priceRange: "$8 – $16 per sq. ft. installed",
    includes: [
      "Solid and engineered hardwood installation, nail-down or glue-down",
      "Board repair, replacement and loose-board securing",
      "Stair treads, risers and custom nosing",
      "Custom borders, inlays and feature strips",
      "Subfloor prep, leveling and squeak reduction",
      "Moisture testing and acclimation",
      "Transitions, thresholds, base shoe and vents",
      "Furniture moving, floor protection and full cleanup",
    ],
    problems: [
      "Scratched, dull or water-stained boards",
      "Squeaking, cupping or crowning planks",
      "Outdated or mismatched stain colors",
      "Gaps at walls, worn transitions and missing nosing",
      "Damaged or mismatched boards from past repairs",
      "Carpets hiding original hardwood that could be restored",
    ],
    projects: [
      "Whole-home hardwood installation",
      "Carpet-to-hardwood conversions",
      "Wide-plank engineered floors",
      "Staircase hardwood (treads, risers and nosing)",
      "Room additions and extensions matching existing floors",
      "Custom borders and inlaid feature strips",
      "Great room and open-concept installs",
    ],
    materials: [
      "Red oak, white oak, maple, hickory and walnut",
      "Solid 3/4\" traditional and wide-plank engineered",
      "Prefinished and site-finished options",
      "Water-based and oil-modified polyurethane",
      "Custom stain matching and white/grey washes",
      "Felt paper moisture barriers and rosin paper",
    ],
    process: [
      { title: "In-Home Consultation", desc: "We bring wood samples and stain swatches to your home, measure the rooms, and test the subfloor and crawl space for moisture — no obligation." },
      { title: "Species & Finish Selection", desc: "Choose solid vs. engineered, the species, plank width, grade and finish — prefinished for speed or site-finished for a custom, seamless look." },
      { title: "Acclimation & Subfloor Prep", desc: "Wood is delivered early to acclimate to your home's humidity. We level the subfloor, secure loose boards and address squeaks before a single plank goes down." },
      { title: "Installation", desc: "Planks are nailed or glued per the manufacturer's spec, with proper expansion gaps, staggered joints and matched transitions at every doorway." },
      { title: "Sanding & Finishing (site-finished)", desc: "For site-finished floors we sand smooth, apply your chosen stain, and coat with water-based or oil-modified polyurethane for a durable, even sheen." },
      { title: "Final Walkthrough", desc: "We clean the floors, review care and humidity guidelines, and inspect every board and transition with you before we leave." },
    ],
    priceFactors: [
      "Square footage and number of rooms",
      "Solid vs. engineered construction",
      "Species, grade and plank width",
      "Prefinished vs. site-finished",
      "Subfloor prep, leveling and squeak repair required",
      "Stairs, custom borders and inlays",
      "Stain color and number of finish coats",
    ],
    faqs: [
      { q: "How long does hardwood flooring installation take?", a: "Most homes are installed in 2–4 days depending on square footage and subfloor prep; site-finishing adds 1–2 days for sanding and drying." },
      { q: "Do you move the furniture?", a: "Yes. Our crews move and protect your furniture and put it back when the job is complete." },
      { q: "Solid or engineered hardwood — which is better?", a: "Solid wood can be refinished more times; engineered is more stable over concrete slabs and in basements and handles humidity swings better." },
      { q: "How much does hardwood flooring cost in Danbury, CT?", a: "Most hardwood projects in our area run $8–$16 per square foot installed depending on species, width and prep." },
      { q: "Can you match my existing hardwood?", a: "Usually yes — we match species, plank width and stain color when extending floors into new rooms or repairing damaged areas." },
      { q: "How long before I can walk on the floors?", a: "Light foot traffic is usually fine 24 hours after the final coat; furniture goes back after 3–5 days. Area rugs should wait 2–4 weeks." },
      { q: "Do you install hardwood on stairs?", a: "Yes, including treads, risers, custom nosing and matching stain for a seamless transition from the floor." },
      { q: "Is financing available?", a: "Yes — we offer financing options so you can spread the investment over time." },
      { q: "Prefinished or site-finished — which should I choose?", a: "Prefinished installs faster with no dust or odor; site-finished gives a seamless, custom look and lets you choose any stain and sheen." },
      { q: "Do hardwood floors add resale value?", a: "Yes — hardwood is one of the few flooring upgrades that consistently returns value at resale and appeals to Connecticut buyers." },
      { q: "Can hardwood go in a basement?", a: "Engineered hardwood can go over a dry, conditioned concrete slab; solid hardwood is not recommended below grade." },
      { q: "How do I care for new hardwood floors?", a: "Sweep or vacuum with a hard-floor setting, damp-mop sparingly with a hardwood cleaner, use felt pads, and keep indoor humidity between 35–55%." },
    ],
  },
  {
    slug: "laminate-flooring",
    name: "Laminate",
    short: "Affordable, scratch-resistant laminate flooring installation with realistic wood looks.",
    hero: "The look of hardwood at a budget that works.",
    priority: 3,
    priceRange: "$3 – $7 per sq. ft. installed",
    includes: [
      "Click-lock laminate installation, floating floor",
      "Premium underlayment for sound and moisture control",
      "Subfloor prep, leveling and flatness check",
      "Removal and haul-away of old carpet or flooring",
      "Transitions, thresholds, T-moldings and trim",
      "Moisture barrier over concrete",
      "Stair tread laminate and riser finishing",
      "Furniture moving, floor protection and full cleanup",
    ],
    problems: [
      "Tired, stained or worn carpet in high-traffic rooms",
      "Tight remodeling or rental-flip budgets",
      "Scratches and gouges from pets, kids and furniture",
      "Noisy, hollow-sounding floors in upstairs rooms",
      "Damaged laminate from past water exposure",
      "Uneven concrete or wood subfloors needing flatness work",
    ],
    projects: [
      "Living room and bedroom laminate",
      "Full-floor rental and apartment upgrades",
      "Hallways, stairs and landings",
      "Home office and basement conversions",
      "Whole-home laminate on a budget",
      "Kid and pet playroom flooring",
      "Quick resale and turnover flips",
    ],
    materials: [
      "AC4/AC5 commercial-rated laminate",
      "Water-resistant and waterproof-core options",
      "Foam, cork and acoustic underlayment",
      "All major laminate brands in stock",
      "Embossed-in-register wood and stone textures",
      "Matching T-moldings, reducers and stair nosing",
    ],
    process: [
      { title: "In-Home Consultation", desc: "We bring laminate samples in every price point to your home, measure the rooms, and check the subfloor for flatness — no obligation." },
      { title: "Product Selection", desc: "Pick the AC rating, color and texture, water-resistant core, and underlayment grade that fit your traffic, budget and pets." },
      { title: "Subfloor Prep", desc: "We flatten high and low spots, install a moisture barrier over concrete, and lay premium underlayment for sound control." },
      { title: "Removal & Demo", desc: "Old carpet, pad or laminate comes up and is hauled away; tack strips and staples are removed so the subfloor is clean." },
      { title: "Installation", desc: "Planks click together as a floating floor with proper expansion gaps, staggered joints, and matched transitions and trim at every doorway." },
      { title: "Final Walkthrough", desc: "We clean the floor, inspect transitions and trim with you, and review cleaning and care before we finish." },
    ],
    priceFactors: [
      "Square footage and number of rooms",
      "AC wear rating (AC4 vs. AC5)",
      "Underlayment grade and moisture barrier",
      "Subfloor flatness and prep required",
      "Removal and disposal of existing flooring",
      "Room count, transitions and stair work",
      "Water-resistant vs. standard core",
    ],
    faqs: [
      { q: "How much does laminate flooring cost to install?", a: "Most laminate projects run $3–$7 per square foot installed, making it our most budget-friendly wood-look option." },
      { q: "Is laminate water resistant?", a: "Many modern laminates are water resistant, and some have waterproof cores. For full waterproof performance in wet areas we recommend luxury vinyl." },
      { q: "How durable is laminate flooring?", a: "AC4 and AC5 rated laminate is highly scratch and impact resistant, rated for heavy residential and light commercial traffic — ideal for pets and kids." },
      { q: "Can laminate be installed over concrete?", a: "Yes, with a moisture barrier and proper underlayment to prevent wicking and dampen sound." },
      { q: "How long does installation take?", a: "Typical homes are finished in 1–2 days; larger whole-home installs may take 2–3 days." },
      { q: "Will laminate sound hollow?", a: "Not with quality underlayment — we always include a sound-dampening layer so the floor feels solid underfoot." },
      { q: "Can laminate be refinished?", a: "No. Laminate is replaced rather than refinished, which is why the lower upfront cost and easy plank replacement matter." },
      { q: "Do you offer same-day appointments?", a: "Yes — we bring samples to you with same-day appointments and next-day installation on in-stock materials." },
      { q: "Can I install laminate in a kitchen or bathroom?", a: "Water-resistant laminate works in kitchens; for full baths and wet areas we recommend waterproof luxury vinyl instead." },
      { q: "How realistic does laminate look?", a: "Today's embossed-in-register laminate closely matches real wood grain and texture, often indistinguishable from hardwood at a glance." },
      { q: "Can a single damaged plank be replaced?", a: "Yes — individual planks can be replaced, though matching later lots can vary, so it's wise to keep a spare box." },
      { q: "How do I clean laminate floors?", a: "Sweep or vacuum regularly, damp-mop with a laminate-safe cleaner, and wipe up spills promptly. Avoid steam mops and excess water." },
    ],
  },
  {
    slug: "floor-refinishing",
    name: "Floor Refinishing",
    short: "Hardwood floor sanding, staining and refinishing — bring worn floors back to life without replacing them.",
    hero: "Don't replace them — refinish them and save thousands.",
    priority: 4,
    priceRange: "$3 – $6 per sq. ft.",
    includes: [
      "Dustless sanding, edging and corner detail",
      "Custom staining and color matching",
      "Water-based and oil-modified polyurethane coats",
      "Buff and recoat for lightly worn floors",
      "Board repair, replacement and gap filling",
      "Stair tread and riser refinishing",
      "Furniture moving, masking and full cleanup",
      "Subfloor and squeak checks during prep",
    ],
    problems: [
      "Deep scratches, dents and pet damage",
      "Grey, dull or sun-faded finish",
      "Water stains and dark pet spots",
      "Dated stain colors from decades ago",
      "Worn finish in high-traffic hallways and entries",
      "Squeaky or loose boards revealed during sanding",
    ],
    projects: [
      "Full sand and refinish of existing hardwood",
      "Buff and recoat for lightly worn floors",
      "Stair refinishing and tread replacement",
      "Original hardwood revealed under carpet",
      "Color change from dark to light or vice versa",
      "Blending patched areas with existing floors",
      "Whole-home refinish before a sale",
    ],
    materials: [
      "Dust-containment sanding systems",
      "Custom stain matching and water-based dyes",
      "Low-VOC water-based polyurethane",
      "Oil-modified polyurethane for warm amber tone",
      "Wood filler, color-matched putty and trowel fill",
      "Screening pads and abrasives for buff and recoat",
    ],
    process: [
      { title: "Assessment & Quote", desc: "We inspect the existing hardwood, measure the area, check board thickness and flag any boards too thin to sand — then give you a clear, itemized quote." },
      { title: "Prep & Protection", desc: "We move and mask furniture, seal off vents and doorways, and contain the work zone so dust stays out of the rest of your home." },
      { title: "Dustless Sanding", desc: "We sand in multiple passes from coarse to fine, edge the perimeter, and detail corners — all with dust-containment equipment for a clean job site." },
      { title: "Repair & Stain", desc: "We replace damaged boards, fill gaps, hand-scrape corners, then apply your chosen stain or leave it natural, matching adjacent rooms when needed." },
      { title: "Finishing Coats", desc: "We apply 2–3 coats of water-based or oil-modified polyurethane, screening between coats for a smooth, even, durable sheen." },
      { title: "Cure & Walkthrough", desc: "We review cure-time and care guidelines, inspect the finish with you, and remove all masking and equipment before we leave." },
    ],
    priceFactors: [
      "Square footage and number of rooms",
      "Number of sanding passes needed (condition of existing floor)",
      "Stain vs. natural finish and number of color samples",
      "Board repairs, replacement and gap filling required",
      "Stairs and landings included",
      "Water-based vs. oil-modified polyurethane and coat count",
      "Furniture moving and masking complexity",
    ],
    faqs: [
      { q: "Can you refinish my existing hardwood instead of replacing it?", a: "Usually yes. If the boards are structurally sound and thick enough, sanding and refinishing costs far less than replacement." },
      { q: "How much does floor refinishing cost?", a: "Most refinishing projects run $3–$6 per square foot depending on condition, stain and coat count; stairs add to the total." },
      { q: "How long does refinishing take?", a: "Typically 2–4 days including sanding, staining and drying time between coats; larger whole-home jobs may run a day longer." },
      { q: "Do you offer dustless sanding?", a: "Yes. We use dust-containment sanders and seal off the work zone to keep your home as clean as possible." },
      { q: "How many times can hardwood be refinished?", a: "Solid hardwood can typically be refinished 4–6 times; engineered depends on the wear-layer thickness — we measure before we start." },
      { q: "Can you change the color of my floors?", a: "Yes — we sand to bare wood and can match nearly any stain color, including modern white, grey and dark espresso washes." },
      { q: "Do we need to move out?", a: "No, but we recommend staying off the floors during coating and drying. We plan the schedule and access around your household." },
      { q: "What's a buff and recoat?", a: "For floors with surface-level wear, we lightly abrade the finish and apply a fresh coat — faster and cheaper than a full sand, and it extends the floor's life." },
      { q: "How long before I can walk on refinished floors?", a: "Socks-only traffic is usually fine after 24 hours; furniture goes back after 3–5 days, and area rugs after 2–4 weeks for full curing." },
      { q: "Will refinishing remove water and pet stains?", a: "Most surface stains sand out completely; deep pet stains may require board replacement, which we handle as part of the repair step." },
      { q: "Is refinishing worth it before selling?", a: "Yes — a freshly refinished floor is one of the highest-ROI upgrades for a Connecticut home sale and photographs far better in listings." },
      { q: "How long does a refinished floor last?", a: "A properly maintained refinish typically lasts 7–10 years in busy homes and longer with regular care and felt pads under furniture." },
    ],
  },
  {
    slug: "luxury-vinyl-plank",
    name: "Luxury Vinyl Plank",
    short: "Waterproof luxury vinyl plank (LVP) installation — durable, pet-proof and the most popular floor we install.",
    hero: "Waterproof wood-look planks that handle real life.",
    priority: 5,
    priceRange: "$4 – $9 per sq. ft. installed",
    includes: [
      "Luxury vinyl plank (LVP) installation, floating or glue-down",
      "Rigid-core SPC and WPC planks in wood-look widths",
      "Subfloor leveling, patching and moisture barrier",
      "Removal and haul-away of old carpet, vinyl or laminate",
      "Baseboard, base shoe and quarter-round finishing",
      "Stair tread and riser LVP finishing",
      "Transitions, reducers and thresholds",
      "Furniture moving, floor protection and full cleanup",
    ],
    problems: [
      "Water damage from pets, kids or spills",
      "Old carpet trapping dust, allergens and odors",
      "Uneven, hollow-sounding or squeaky subfloors",
      "Basement moisture ruining wood floors",
      "Scratched or worn laminate in busy homes",
      "Cold, hard tile that's uncomfortable underfoot",
    ],
    projects: [
      "Whole-floor LVP replacement",
      "Carpet-to-LVP conversions",
      "Basement and lower-level waterproof flooring",
      "Rental and condo turnovers",
      "Pet-friendly home flooring",
      "Kitchen, bath and laundry room LVP",
      "Whole-home LVP on one level",
    ],
    materials: [
      "Rigid-core SPC and WPC planks",
      "Wood-look wide planks in multiple widths",
      "20-mil and 30-mil commercial wear layers",
      "Attached acoustic underlayment",
      "Glue-down LVP for permanent and stair installs",
      "Matching stair nosing, reducers and T-moldings",
    ],
    process: [
      { title: "In-Home Consultation", desc: "We bring wood-look LVP samples in every color and width to your home, measure the rooms, and check the subfloor for flatness and moisture — no obligation." },
      { title: "Product Selection", desc: "Pick the plank width, color, wear layer and thickness that fit your traffic, pets and budget, plus floating vs. glue-down." },
      { title: "Subfloor Prep", desc: "We flatten the subfloor, patch low spots, apply a moisture barrier over concrete, and address any hollow or squeaky areas." },
      { title: "Removal & Demo", desc: "Old carpet, pad, vinyl or laminate comes up and is hauled away; the subfloor is cleaned and prepped for a flat install." },
      { title: "Installation", desc: "Planks lock together as a waterproof floating floor (or glue-down) with proper expansion gaps, staggered joints, and matched transitions and trim." },
      { title: "Final Walkthrough", desc: "We clean the floor, inspect every plank and transition with you, and review cleaning and care before we finish." },
    ],
    priceFactors: [
      "Square footage and number of rooms",
      "Plank thickness and wear-layer mil rating",
      "Subfloor leveling and moisture prep needed",
      "Removal and disposal of existing flooring",
      "Stairs, nosing and transitions",
      "Floating vs. glue-down installation",
      "Base shoe and quarter-round replacement",
    ],
    faqs: [
      { q: "Is luxury vinyl plank really waterproof?", a: "Rigid-core LVP is 100% waterproof, which makes it ideal for kitchens, baths, basements and homes with pets — spills sit on top and wipe away." },
      { q: "How fast can LVP be installed?", a: "Most homes are completed in 1–3 days, and we offer next-day installation on in-stock materials." },
      { q: "Does LVP scratch?", a: "Quality LVP with a 20-mil wear layer resists scratches from pets and furniture far better than laminate; commercial 30-mil options are even tougher." },
      { q: "How much does LVP cost?", a: "Installed LVP typically runs $4–$9 per square foot depending on the wear layer, thickness and subfloor prep." },
      { q: "What's the difference between LVP and LVT?", a: "Same waterproof rigid-core technology — LVP comes in wood-look planks, LVT in stone- and tile-look squares (often groutable)." },
      { q: "Is LVP good for basements?", a: "Yes — it's the most reliable choice for below-grade concrete slabs because it won't warp, swell or cup from moisture." },
      { q: "Do you remove the old carpet?", a: "Yes. Removal and disposal of your existing flooring, pad and tack strips is included in the estimate." },
      { q: "Can you match samples to my furniture?", a: "We bring hundreds of samples to your home so you can compare colors and textures in your own lighting and against your décor." },
      { q: "Can LVP go over my existing floor?", a: "Often yes, if the existing floor is flat, dry and sound. Floating LVP can install over intact vinyl or laminate; we confirm during the estimate." },
      { q: "How realistic does LVP look?", a: "Today's embossed, registered-emboss LVP closely matches real wood grain, knots and texture, often indistinguishable from hardwood at a glance." },
      { q: "Is LVP quiet underfoot?", a: "Yes — most rigid-core LVP has an attached acoustic underlayment, and we add sound control over concrete for a solid, quiet feel." },
      { q: "How do I clean and maintain LVP?", a: "Sweep or vacuum regularly, damp-mop with a neutral cleaner, wipe up spills promptly, and use felt pads under furniture. Avoid wax and steam mops." },
    ],
  },
];

export const SERVICE_AREAS = [
  {
    slug: "danbury",
    name: "Danbury",
    state: "CT",
    intro: "Headquartered on Starr Road in Danbury, we install and refinish floors across the city every week.",
    neighborhoods: ["Downtown Danbury", "Mill Plain", "King Street", "Great Plain", "Candlewood Lake", "Westville"],
    zips: ["06810", "06811", "06813"],
    localContext: "Many Danbury homes are colonials and capes built between 1950 and 1990 with original hardwood under the carpet. Refinishing what's already there is often the fastest, most affordable upgrade.",
  },
  {
    slug: "ridgefield",
    name: "Ridgefield",
    state: "CT",
    intro: "Ridgefield homeowners call us for wide-plank hardwood, floor refinishing and luxury vinyl.",
    neighborhoods: ["Main Street Village", "Ridgebury", "Farmingville", "Titicus", "Branchville"],
    zips: ["06877"],
    localContext: "Ridgefield's older colonials and antique homes need careful sanding, board matching and stain matching to preserve original character.",
  },
  {
    slug: "bethel",
    name: "Bethel",
    state: "CT",
    intro: "From carpet-to-vinyl conversions to full hardwood installs, we're in Bethel constantly.",
    neighborhoods: ["Bethel Center", "Stony Hill", "Plumtrees", "Grassy Plain"],
    zips: ["06801"],
    localContext: "Bethel's mix of split-levels and newer construction makes luxury vinyl plank and laminate especially popular for family homes.",
  },
  {
    slug: "brookfield",
    name: "Brookfield",
    state: "CT",
    intro: "Brookfield families trust us for waterproof flooring that survives kids, pets and lake season.",
    neighborhoods: ["Brookfield Center", "Candlewood Shores", "Obtuse Hill", "High Meadow"],
    zips: ["06804"],
    localContext: "With Candlewood Lake nearby, moisture-resistant luxury vinyl and porcelain tile are the go-to choices in Brookfield homes.",
  },
  {
    slug: "newtown",
    name: "Newtown",
    state: "CT",
    intro: "We serve Newtown and Sandy Hook with hardwood installation, floor refinishing and luxury vinyl.",
    neighborhoods: ["Sandy Hook", "Hawleyville", "Botsford", "Dodgingtown", "Newtown Borough"],
    zips: ["06470", "06482"],
    localContext: "Newtown's larger properties and older farmhouses often call for wide-plank hardwood and full-house refinishing projects.",
  },
  {
    slug: "greenwich",
    name: "Greenwich",
    state: "CT",
    intro: "Homeowners and businesses in Greenwich trust VRP for hardwood, luxury vinyl, laminate and floor refinishing.",
    neighborhoods: ["Downtown", "Belle Haven", "Cos Cob", "Riverside", "Old Greenwich"],
    zips: ["06830", "06831"],
    localContext: "Greenwich's mix of historic estates and modern builds calls for premium wide-plank hardwood and refinishing that preserves original character.",
  },
  {
    slug: "stamford",
    name: "Stamford",
    state: "CT",
    intro: "From carpet-to-hardwood conversions to full refinishing, we install floors across Stamford every week.",
    neighborhoods: ["Downtown", "Springdale", "Shippan", "Newfield", "North Stamford"],
    zips: ["06901", "06902", "06905", "06907"],
    localContext: "Stamford's high-rise condos and single-family homes alike favor waterproof luxury vinyl plank for low-maintenance, pet-friendly living.",
  },
  {
    slug: "darien",
    name: "Darien",
    state: "CT",
    intro: "We bring the showroom to Darien — hardwood, vinyl and laminate samples measured and priced in your home.",
    neighborhoods: ["Noroton", "Noroton Heights", "Tokeneke", "Long Neck"],
    zips: ["06820"],
    localContext: "Darien's colonials and contemporaries often need board repair and stain matching when extending original hardwood into renovated spaces.",
  },
  {
    slug: "new-canaan",
    name: "New Canaan",
    state: "CT",
    intro: "Licensed, insured flooring installation and refinishing for homes and businesses in New Canaan.",
    neighborhoods: ["Town Center", "Talmadge Hill", "Silvermine"],
    zips: ["06840"],
    localContext: "New Canaan's mid-century and antique homes benefit from dustless refinishing that protects moldings and cabinetry during sanding.",
  },
  {
    slug: "westport",
    name: "Westport",
    state: "CT",
    intro: "Homeowners and businesses in Westport trust VRP for hardwood, luxury vinyl, laminate and floor refinishing.",
    neighborhoods: ["Compo Beach", "Saugatuck", "Greens Farms", "Old Hill"],
    zips: ["06880"],
    localContext: "Westport's shore-side homes need moisture-resistant luxury vinyl and tile for entries, mudrooms and lower levels.",
  },
  {
    slug: "wilton",
    name: "Wilton",
    state: "CT",
    intro: "From carpet-to-hardwood conversions to full refinishing, we install floors across Wilton every week.",
    neighborhoods: ["Wilton Center", "Georgetown", "Cannondale"],
    zips: ["06897"],
    localContext: "Wilton's rural colonials and antique properties often hide original hardwood under carpet, ready for sand-and-refinish restoration.",
  },
  {
    slug: "weston",
    name: "Weston",
    state: "CT",
    intro: "We bring the showroom to Weston — hardwood, vinyl and laminate samples measured and priced in your home.",
    neighborhoods: ["Georgetown", "Lyons Plain", "Aspetuck"],
    zips: ["06883"],
    localContext: "Weston's wooded properties and basements make waterproof rigid-core vinyl the practical choice below grade.",
  },
  {
    slug: "fairfield-ct",
    name: "Fairfield",
    state: "CT",
    intro: "Licensed, insured flooring installation and refinishing for homes and businesses in Fairfield.",
    neighborhoods: ["Fairfield Center", "Southport", "Greenfield Hill", "Stratfield"],
    zips: ["06824", "06825", "06828"],
    localContext: "Fairfield's beach homes and older colonials need flooring that handles humidity and foot traffic from growing families.",
  },
  {
    slug: "easton",
    name: "Easton",
    state: "CT",
    intro: "Homeowners and businesses in Easton trust VRP for hardwood, luxury vinyl, laminate and floor refinishing.",
    neighborhoods: ["Aspetuck", "Plattsville"],
    zips: ["06612"],
    localContext: "Easton's rural homes and horse properties often choose durable laminate and hardwood for open living spaces.",
  },
  {
    slug: "trumbull",
    name: "Trumbull",
    state: "CT",
    intro: "From carpet-to-hardwood conversions to full refinishing, we install floors across Trumbull every week.",
    neighborhoods: ["Long Hill", "Nichols", "Tashua", "Beacon Hill"],
    zips: ["06611"],
    localContext: "Trumbull's family homes favor scratch-resistant luxury vinyl plank that stands up to kids and pets.",
  },
  {
    slug: "new-haven",
    name: "New Haven",
    state: "CT",
    intro: "We bring the showroom to New Haven — hardwood, vinyl and laminate samples measured and priced in your home.",
    neighborhoods: ["Downtown", "East Rock", "Westville", "Wooster Square"],
    zips: ["06511", "06513", "06515"],
    localContext: "New Haven's older housing stock and apartments benefit from affordable laminate and waterproof vinyl for quick turnovers.",
  },
  {
    slug: "branford",
    name: "Branford",
    state: "CT",
    intro: "Licensed, insured flooring installation and refinishing for homes and businesses in Branford.",
    neighborhoods: ["Branford Center", "Pine Orchard", "Stony Creek", "Indian Neck"],
    zips: ["06405"],
    localContext: "Branford's shore homes need moisture-rated luxury vinyl and tile for kitchens and entries near the water.",
  },
  {
    slug: "guilford",
    name: "Guilford",
    state: "CT",
    intro: "Homeowners and businesses in Guilford trust VRP for hardwood, luxury vinyl, laminate and floor refinishing.",
    neighborhoods: ["Guilford Center", "Leete's Island", "Jacobs Beach", "North Guilford"],
    zips: ["06437"],
    localContext: "Guilford's historic homes along the shoreline need careful refinishing and board matching to preserve period detail.",
  },
  {
    slug: "madison",
    name: "Madison",
    state: "CT",
    intro: "From carpet-to-hardwood conversions to full refinishing, we install floors across Madison every week.",
    neighborhoods: ["Madison Center", "East River", "North Madison"],
    zips: ["06443"],
    localContext: "Madison's beach cottages and colonials favor waterproof flooring that survives sand, salt and summer traffic.",
  },
  {
    slug: "north-haven",
    name: "North Haven",
    state: "CT",
    intro: "We bring the showroom to North Haven — hardwood, vinyl and laminate samples measured and priced in your home.",
    neighborhoods: ["North Haven Center", "Montowese", "Clintonville"],
    zips: ["06473"],
    localContext: "North Haven's split-levels and newer builds need full floor replacement with modern luxury vinyl and hardwood.",
  },
  {
    slug: "hamden",
    name: "Hamden",
    state: "CT",
    intro: "Licensed, insured flooring installation and refinishing for homes and businesses in Hamden.",
    neighborhoods: ["Hamden Center", "Whitneyville", "Spring Glen", "Mount Carmel"],
    zips: ["06514", "06517", "06518"],
    localContext: "Hamden's mix of apartments and family homes needs durable flooring with fast installation for busy households.",
  },
  {
    slug: "cheshire",
    name: "Cheshire",
    state: "CT",
    intro: "Homeowners and businesses in Cheshire trust VRP for hardwood, luxury vinyl, laminate and floor refinishing.",
    neighborhoods: ["Cheshire Center", "Highland", "West Cheshire"],
    zips: ["06410"],
    localContext: "Cheshire's colonials and raised ranches commonly choose hardwood and laminate for whole-home upgrades.",
  },
  {
    slug: "orange-ct",
    name: "Orange",
    state: "CT",
    intro: "From carpet-to-hardwood conversions to full refinishing, we install floors across Orange every week.",
    neighborhoods: ["Orange Center", "Turkey Hill", "Derby Junction"],
    zips: ["06477"],
    localContext: "Orange's newer construction and family homes need flooring installed to match existing transitions and trim.",
  },
  {
    slug: "woodbridge",
    name: "Woodbridge",
    state: "CT",
    intro: "We bring the showroom to Woodbridge — hardwood, vinyl and laminate samples measured and priced in your home.",
    neighborhoods: ["Woodbridge Center", "Derby Junction"],
    zips: ["06525"],
    localContext: "Woodbridge's larger properties often call for wide-plank hardwood and custom stain matching across open floor plans.",
  },
  {
    slug: "milford",
    name: "Milford",
    state: "CT",
    intro: "Licensed, insured flooring installation and refinishing for homes and businesses in Milford.",
    neighborhoods: ["Milford Center", "Woodmont", "Devon", "Walnut Beach"],
    zips: ["06460"],
    localContext: "Milford's shoreline homes need waterproof luxury vinyl and tile for basements, baths and beach-side entries.",
  },
  {
    slug: "southbury",
    name: "Southbury",
    state: "CT",
    intro: "Homeowners and businesses in Southbury trust VRP for hardwood, luxury vinyl, laminate and floor refinishing.",
    neighborhoods: ["Southbury Center", "Heritage Village", "Kettletown"],
    zips: ["06488"],
    localContext: "Southbury's active-adult communities and colonials favor low-maintenance luxury vinyl and laminate.",
  },
  {
    slug: "prospect",
    name: "Prospect",
    state: "CT",
    intro: "From carpet-to-hardwood conversions to full refinishing, we install floors across Prospect every week.",
    neighborhoods: ["Prospect Center", "Hotchkissville"],
    zips: ["06712"],
    localContext: "Prospect's hilltop homes and new builds need subfloor prep and durable flooring for growing families.",
  },
  {
    slug: "beacon-falls",
    name: "Beacon Falls",
    state: "CT",
    intro: "We bring the showroom to Beacon Falls — hardwood, vinyl and laminate samples measured and priced in your home.",
    neighborhoods: ["Beacon Falls Center", "Pines Bridge"],
    zips: ["06403"],
    localContext: "Beacon Falls' rural homes along the Naugatuck River favor moisture-resistant vinyl for lower levels.",
  },
  {
    slug: "middlebury",
    name: "Middlebury",
    state: "CT",
    intro: "Licensed, insured flooring installation and refinishing for homes and businesses in Middlebury.",
    neighborhoods: ["Middlebury Center", "Meadowview"],
    zips: ["06762"],
    localContext: "Middlebury's lakeside and suburban homes need flooring that handles humidity near Lake Quassapaug.",
  },
  {
    slug: "oxford",
    name: "Oxford",
    state: "CT",
    intro: "Homeowners and businesses in Oxford trust VRP for hardwood, luxury vinyl, laminate and floor refinishing.",
    neighborhoods: ["Oxford Center", "Quaker Farms", "Riverside"],
    zips: ["06478"],
    localContext: "Oxford's growing neighborhoods and new construction need whole-home flooring packages from one crew.",
  },
  {
    slug: "west-hartford",
    name: "West Hartford",
    state: "CT",
    intro: "From carpet-to-hardwood conversions to full refinishing, we install floors across West Hartford every week.",
    neighborhoods: ["Center", "Elmwood", "Bishops Corner", "Morley"],
    zips: ["06107", "06110", "06117", "06119"],
    localContext: "West Hartford's colonials and capes often hide original hardwood under carpet, ready for refinishing.",
  },
  {
    slug: "avon",
    name: "Avon",
    state: "CT",
    intro: "We bring the showroom to Avon — hardwood, vinyl and laminate samples measured and priced in your home.",
    neighborhoods: ["Avon Center", "Pine Hills", "Countryside", "Avon Old Farms"],
    zips: ["06001"],
    localContext: "Avon's upscale homes favor wide-plank engineered hardwood and custom stain matching.",
  },
  {
    slug: "simsbury",
    name: "Simsbury",
    state: "CT",
    intro: "Licensed, insured flooring installation and refinishing for homes and businesses in Simsbury.",
    neighborhoods: ["Simsbury Center", "Tariffville", "Weatogue", "West Simsbury"],
    zips: ["06070"],
    localContext: "Simsbury's historic and river-town homes need careful refinishing to preserve original detail.",
  },
  {
    slug: "farmington",
    name: "Farmington",
    state: "CT",
    intro: "Homeowners and businesses in Farmington trust VRP for hardwood, luxury vinyl, laminate and floor refinishing.",
    neighborhoods: ["Farmington Center", "Unionville", "Farmington Woods"],
    zips: ["06032"],
    localContext: "Farmington's colonials and condos need flooring transitions handled cleanly across open floor plans.",
  },
  {
    slug: "glastonbury",
    name: "Glastonbury",
    state: "CT",
    intro: "From carpet-to-hardwood conversions to full refinishing, we install floors across Glastonbury every week.",
    neighborhoods: ["Glastonbury Center", "South Glastonbury", "Nayaug", "Minnechaug"],
    zips: ["06033"],
    localContext: "Glastonbury's family homes favor durable luxury vinyl plank and hardwood for high-traffic living areas.",
  },
  {
    slug: "rocky-hill",
    name: "Rocky Hill",
    state: "CT",
    intro: "We bring the showroom to Rocky Hill — hardwood, vinyl and laminate samples measured and priced in your home.",
    neighborhoods: ["Rocky Hill Center", "Dividend", "Forge Park"],
    zips: ["06067"],
    localContext: "Rocky Hill's ranches and colonials need full floor replacement and refinishing for updated living spaces.",
  },
  {
    slug: "wethersfield",
    name: "Wethersfield",
    state: "CT",
    intro: "Licensed, insured flooring installation and refinishing for homes and businesses in Wethersfield.",
    neighborhoods: ["Old Wethersfield", "Highcrest", "Silas Deane"],
    zips: ["06109"],
    localContext: "Wethersfield's historic district homes need board matching and refinishing that respects period detail.",
  },
  {
    slug: "berlin",
    name: "Berlin",
    state: "CT",
    intro: "Homeowners and businesses in Berlin trust VRP for hardwood, luxury vinyl, laminate and floor refinishing.",
    neighborhoods: ["Berlin Center", "East Berlin", "Kensington", "Beckley"],
    zips: ["06037"],
    localContext: "Berlin's family homes and raised ranches favor laminate and vinyl for budget-friendly whole-home upgrades.",
  },
  {
    slug: "southington",
    name: "Southington",
    state: "CT",
    intro: "From carpet-to-hardwood conversions to full refinishing, we install floors across Southington every week.",
    neighborhoods: ["Southington Center", "Plantsville", "Marion", "Milldale"],
    zips: ["06489"],
    localContext: "Southington's suburban homes need scratch-resistant flooring that handles busy family life.",
  },
  {
    slug: "canton",
    name: "Canton",
    state: "CT",
    intro: "We bring the showroom to Canton — hardwood, vinyl and laminate samples measured and priced in your home.",
    neighborhoods: ["Collinsville", "Canton Center", "North Canton", "Canton Valley"],
    zips: ["06019"],
    localContext: "Canton's hillside and river-town homes favor engineered hardwood and waterproof vinyl for lower levels.",
  },
];

export const COMBO_PAGES = [
  { service: "hardwood-flooring", area: "danbury" },
  { service: "luxury-vinyl-plank", area: "danbury" },
  { service: "floor-refinishing", area: "ridgefield" },
];

export const REVIEWS = [
  {
    name: "John Scavelli",
    location: "Danbury, CT",
    rating: 5,
    text: "The team demonstrated professionalism and attention to detail from start to finish. They arrived on time, communicated clearly, and treated our home with great care. The finished floors looked great and were beautifully finished.",
    project: "Hardwood Buff & Refinish",
  },
  {
    name: "David Angley",
    location: "Brookfield, CT",
    rating: 5,
    text: "VRP Flooring replaced my entire first floor with LVP that transformed our space into a beautifully crafted home. They respected our furniture and valuables when moving them and placing them back. Very satisfied with the quality and cost.",
    project: "Luxury Vinyl Plank Replacement",
  },
  {
    name: "Maria Ordonez",
    location: "Bethel, CT",
    rating: 5,
    text: "Very professional and easy to work with. They have a great selection of different quality and price points. I could not have been happier with my decision. I will definitely use them again when needed.",
    project: "Flooring Installation",
  },
  {
    name: "Jim O'Sullivan",
    location: "Newtown, CT",
    rating: 5,
    text: "VRP did an excellent job on my oak hardwood floors. Stripping and 3 poly coats refurbished them to brand new after 20 plus years of wear and tear. They were polite, arrived when scheduled and completed all work on time.",
    project: "Oak Hardwood Refinishing",
  },
];

export const FAQ_HOME = [
  { q: "What areas do you serve?", a: "We're based at 6 Whitney Ave in Danbury, CT and serve homeowners and businesses across Fairfield, New Haven and Hartford County — from Greenwich and Stamford to New Haven, West Hartford and the towns in between." },
  { q: "Are you licensed and insured?", a: "Yes. VRP Flooring LLC is a licensed, insured, locally owned and operated flooring contractor. Proof of insurance is available on request." },
  { q: "How does the free estimate work?", a: "Request an estimate online or call us. We bring hundreds of samples to your home or business, measure the space, and give you a written no-obligation estimate — often the same day." },
  { q: "How fast can you install?", a: "We offer convenient same-day appointments with next-day installation on in-stock flooring. Most homes are completed in one to four days." },
  { q: "What flooring types do you install?", a: "Luxury vinyl tile (LVT), hardwood, laminate, luxury vinyl plank (LVP) — plus professional hardwood floor refinishing." },
  { q: "Do you offer financing?", a: "Yes — financing is available so you can spread the cost of your new floors over time." },
];

type BlogPost = { slug: string; title: string; excerpt: string; date: string; readTime: string; category: string };
export const BLOG_POSTS: BlogPost[] = [];
